import type {
  GoalActivityInitiative,
  GoalActivityProject,
  GoalActivityTask,
  ProjectState,
  TicketState,
  VerificationState,
  WorkOrdering,
} from '@localagent/sdk';
import type { Position as OrgPosition } from '@localagent/sdk';
import { BOARD_COLUMNS } from './work-management-helpers';

export type ProjectLane =
  | 'unassigned'
  | 'ready'
  | 'in_progress'
  | 'in_verification'
  | 'done'
  | 'paused'
  | 'cancelled';

export const PROJECT_LANES: ProjectLane[] = [
  'unassigned',
  'ready',
  'in_progress',
  'in_verification',
  'done',
  'paused',
  'cancelled',
];

export const PROJECT_LANE_LABELS: Record<ProjectLane, string> = {
  unassigned: 'Unassigned',
  ready: 'Ready',
  in_progress: 'In progress',
  in_verification: 'In verification',
  done: 'Done',
  paused: 'Paused',
  cancelled: 'Cancelled',
};

export const PROJECT_LANE_COLORS: Record<ProjectLane, string> = {
  unassigned: 'bg-muted text-foreground-secondary',
  ready: 'bg-chart-1/15 text-chart-1',
  in_progress: 'bg-info-subtle text-info-text',
  in_verification: 'bg-chart-6/15 text-chart-6',
  done: 'bg-success-subtle text-success-text',
  paused: 'bg-muted text-foreground-secondary',
  cancelled: 'bg-error-subtle text-error-text',
};

export const VERIFICATION_LABELS: Record<VerificationState, string> = {
  not_ready: 'Not ready to verify',
  awaiting: 'Awaiting assessment',
  accepted: 'Verified',
};

export interface ProjectLaneInput {
  state: ProjectState;
  verification_state: VerificationState;
  assignee_position_id?: string | null;
}

export function projectLane(project: ProjectLaneInput): ProjectLane {
  if (project.state === 'cancelled') return 'cancelled';
  // The assessment gate outranks the state enum, which cannot express it
  // (app-shell ADR-011).
  if (project.verification_state === 'awaiting') return 'in_verification';
  if (project.verification_state === 'accepted') return 'done';

  switch (project.state) {
    case 'completed':
      return 'done';
    case 'paused':
      return 'paused';
    case 'active':
      return 'in_progress';
    case 'planned':
      return project.assignee_position_id ? 'ready' : 'unassigned';
    default: {
      const unreachable: never = project.state;
      throw new Error(`unknown project state ${String(unreachable)}`);
    }
  }
}

export function groupProjectsByLane<T extends ProjectLaneInput>(
  projects: T[],
): Record<ProjectLane, T[]> {
  const lanes = Object.fromEntries(
    PROJECT_LANES.map((lane) => [lane, [] as T[]]),
  ) as Record<ProjectLane, T[]>;
  for (const project of projects) lanes[projectLane(project)].push(project);
  return lanes;
}

export interface GoalProjectEntry {
  project: GoalActivityProject;
  initiativeId: string;
  initiativeName: string;
}

export function goalProjects(
  initiatives: GoalActivityInitiative[] | undefined,
): GoalProjectEntry[] {
  const entries: GoalProjectEntry[] = [];
  for (const initiative of initiatives ?? []) {
    for (const project of initiative.projects) {
      entries.push({
        project,
        initiativeId: initiative.id,
        initiativeName: initiative.name,
      });
    }
  }
  return entries;
}

export interface ProjectTicketCounts {
  total: number;
  done: number;
  byState: Record<TicketState, number>;
}

export function projectTicketCounts(project: { tasks: GoalActivityTask[] }): ProjectTicketCounts {
  const byState = Object.fromEntries(
    BOARD_COLUMNS.map((state) => [state, 0]),
  ) as Record<TicketState, number>;
  for (const task of project.tasks) {
    if (!(task.state in byState)) {
      throw new Error(`task ${task.identifier} has unknown state ${task.state}`);
    }
    byState[task.state] += 1;
  }
  return { total: project.tasks.length, done: byState.done, byState };
}

export interface AssigneeResolver {
  position(id?: string | null): string;
  actor(personaId?: string | null, userId?: string | null): string;
}

export function assigneeResolver(positions: OrgPosition[] | undefined): AssigneeResolver {
  const byPosition = new Map<string, string>();
  const byPersona = new Map<string, string>();
  const byUser = new Map<string, string>();
  for (const position of positions ?? []) {
    byPosition.set(position.id, position.title);
    if (position.persona_id) byPersona.set(position.persona_id, position.title);
    if (position.user_id) byUser.set(position.user_id, position.title);
  }
  return {
    position: (id) => (id ? byPosition.get(id) ?? 'Unknown position' : 'Unassigned'),
    actor: (personaId, userId) => {
      if (personaId) return byPersona.get(personaId) ?? 'Persona';
      if (userId) return byUser.get(userId) ?? 'User';
      return 'Unassigned';
    },
  };
}

export function nodeLabelIndex(
  initiatives: GoalActivityInitiative[] | undefined,
): Map<string, string> {
  const labels = new Map<string, string>();
  for (const initiative of initiatives ?? []) {
    labels.set(initiative.id, initiative.name);
    for (const project of initiative.projects) labels.set(project.id, project.name);
  }
  return labels;
}

export const UNKNOWN_BLOCKER_LABEL = 'Unknown blocker';

export const BLOCKERS_UNAVAILABLE = 'unavailable';

// A failed ordering read leaves blockers UNKNOWN, never absent (.claude/rules/no-fallbacks.md).
export type BlockerList = string[] | typeof BLOCKERS_UNAVAILABLE | undefined;

export function blockerIndex(
  orderings: WorkOrdering[] | undefined,
  labels: Map<string, string>,
): Map<string, string[]> {
  const byBlocked = new Map<string, string[]>();
  for (const ordering of orderings ?? []) {
    // A racing snapshot must not un-block a blocked node (.claude/rules/no-fallbacks.md).
    const label = labels.get(ordering.blocking_node_id) ?? UNKNOWN_BLOCKER_LABEL;
    const existing = byBlocked.get(ordering.blocked_node_id);
    if (existing) existing.push(label);
    else byBlocked.set(ordering.blocked_node_id, [label]);
  }
  return byBlocked;
}
